import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "mb-4"
}
const _hoisted_2 = { class: "mb-3" }
const _hoisted_3 = { class: "heading" }
const _hoisted_4 = {
  id: "address",
  class: "mb-3"
}
const _hoisted_5 = {
  key: 0,
  class: "mx-3 my-2"
}
const _hoisted_6 = {
  id: "city",
  class: "mb-3"
}
const _hoisted_7 = {
  key: 0,
  class: "mx-3 my-2"
}
const _hoisted_8 = {
  id: "postal_code",
  class: "row"
}
const _hoisted_9 = { class: "col-12 col-md-5 mb-3" }
const _hoisted_10 = {
  key: 0,
  class: "mx-3 my-2"
}
const _hoisted_11 = {
  id: "country",
  class: "col-12 col-md-7 mb-3"
}
const _hoisted_12 = {
  id: "phone_number",
  class: "mb-3"
}
const _hoisted_13 = { class: "d-flex flex-row nowrap" }
const _hoisted_14 = { class: "col-6 col-md-8" }
const _hoisted_15 = {
  key: 0,
  class: "mx-3 my-2 position-absolute end-0"
}

import {
    computed,
    ComputedRef,
    onMounted,
    ref,
    Ref,
  } from 'vue'
  import * as _ from 'lodash'
  import phone_codes from '@/config/phone_codes'

  // common components
  import Autocomplete from '@/components/LoginRegister/common/inputs/Autocomplete/v1/Autocomplete.vue'
  import TextInput from '@/components/LoginRegister/common/inputs/Text/v1/Text.vue'
  import FieldError from '@/components/LoginRegister/common/FieldError/v1/FieldError.vue'
  import ContainerBox from '@/components/LoginRegister/common/ContainerBox/v1/ContainerBox.vue'

  // svg components
  import CheckSvg from '@/components/LoginRegister/svg/CheckSvg.vue'

  // config
  import register_form_mandatory from '@/config/loginRegisterLegacy/register_form_mandatory.config'
  import edit_form_mandatory from '@/config/loginRegisterLegacy/edit_form_mandatory.config'
  import defaultPhoneCodes from '@/config/loginRegisterLegacy/defaultPhoneCodes'
  import defaultCountries from '@/config/loginRegisterLegacy/defaultCountries'

  // model
  import RegisterForm from '@/model/Form/RegisterForm'
  import EditForm from '@/model/Form/EditForm'

  // countries
  import countries from 'i18n-iso-countries'
  import {
    isPhoneNumberValid,
    getInvalidPhoneMessage,
  } from '@/components/LoginRegister/phoneNumberValidation'

  // Translations
  import { useI18n } from 'vue-i18n'
  interface Props {
    form: RegisterForm | EditForm
    invalidFields: Set<string>
    showErrors: boolean
  }
  interface Emits {
    (e: 'updateForm', form: RegisterForm | EditForm): void
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'ContactsForm',
  props: {
    form: {},
    invalidFields: {},
    showErrors: { type: Boolean }
  },
  emits: ["updateForm"],
  setup(__props: any, { emit: __emit }) {

  const { t } = useI18n()

  class PhoneCodeAutocompleteOption {
    value: string
    label: string
    code: string
    name?: string
    dial_code?: string
    constructor(
      value: string,
      label: string,
      code: string,
      name?: string,
      dial_code?: string
    ) {
      this.value = value
      this.label = label
      this.name = name
      this.dial_code = dial_code
      this.code = code
    }
  }
  class CountryAutocompleteOption {
    public value: string
    public label: string
    constructor(value: string, label: string) {
      this.value = value
      this.label = label
    }
  }

  // refs
  const phone_codes_prepared: Ref<PhoneCodeAutocompleteOption[]> = ref([])
  const countries_prepared: Ref<CountryAutocompleteOption[]> = ref([])
  const phoneCodesMap_byCountryCode: Ref<
    Map<string, PhoneCodeAutocompleteOption>
  > = ref(new Map())
  const countries_prepared_map: Ref<Map<string, CountryAutocompleteOption>> =
    ref(new Map())

  // props
  const props = __props

  // emit
  const emit = __emit

  const invalidPhoneMessage = computed(() =>
    getInvalidPhoneMessage(t, props.form.phone_code)
  )

  // computed
  const selectedPhoneCodeOption: ComputedRef<
    PhoneCodeAutocompleteOption | undefined
  > = computed(() => {

    return phoneCodesMap_byCountryCode.value.get(props.form.country)
  })
  const selectedCountryOption: ComputedRef<
    CountryAutocompleteOption | undefined
  > = computed(() => {
    return countries_prepared.value.find(countryAutocompleteOption => {
      return (
        convertCountryNameToCode(countryAutocompleteOption.value) ===
        props.form.country
      )
    })
  })
  const mandatoryFieldsSet = computed(() => {
    if (props.form instanceof RegisterForm) {
      return register_form_mandatory
    } else if (props.form instanceof EditForm) {
      return edit_form_mandatory
    } else {
      throw new TypeError('form has to be instance of RegisterForm or EditForm')
    }
  })
  const contentProcessed = computed(() => {
    return {
      address: `${t('loginRegister.contacts_address')}${
        mandatoryFieldsSet.value.has('address') && '*'
      }`,
      city: `${t('loginRegister.contacts_city')}${
        mandatoryFieldsSet.value.has('city') && '*'
      }`,
      country: `${t('loginRegister.contacts_country')}${
        mandatoryFieldsSet.value.has('country') && '*'
      }`,
      postal_code: `${t('loginRegister.contacts_postal_code')}${
        mandatoryFieldsSet.value.has('postal_code') && '*'
      }`,
      phone_number: `${t('loginRegister.contacts_phone_number')}${
        mandatoryFieldsSet.value.has('phone_number') && '*'
      }`,
    }
  })

  const convertCountryNameToCode = (countryName: string) => {
    const lang = window.locale.split('-')[0]
    const formattedCountryName = countryName.includes('/')
      ? countryName.split('/')[0].trim()
      : countryName
    const countryNameToCode = countries.getAlpha2Code(
      formattedCountryName,
      lang
    )
    return countryNameToCode
  }

  // handlers
  const handleTextInputChange = (field: string, newValue: string) => {
    const fieldKey = field as keyof RegisterForm
    const formClone = _.cloneDeep(props.form) as any // stupid annoying TS error
    formClone[fieldKey] = newValue
    emit('updateForm', formClone)
  }
  const handlePhoneCodeChange = (option: PhoneCodeAutocompleteOption) => {
    const formClone = _.cloneDeep(props.form)
    if (!option.dial_code)
      throw new Error('some issue with logic here, my dude')
    formClone.phone_code = option.dial_code
    emit('updateForm', formClone)
  }
  const handleCountryChange = (option: CountryAutocompleteOption) => {
    const formClone = _.cloneDeep(props.form)
    formClone.country = convertCountryNameToCode(option.value)
    emit('updateForm', formClone)
  }

  const initPhoneCodes = async () => {
    // props.form.phone_code
    const lang = document.querySelector('html')!.getAttribute('lang')!
    if (!lang) throw new Error('no lang on html?')
    phone_codes.forEach(codeObj => {
      const phoneCodeAutocompleteOption = new PhoneCodeAutocompleteOption(
        codeObj.name,
        `${codeObj['dial_code']} (${codeObj['code']})`,
        codeObj.code,
        codeObj.name,
        codeObj.dial_code
      )
      phone_codes_prepared.value.push(phoneCodeAutocompleteOption)
      if (!phoneCodeAutocompleteOption.dial_code)
        throw new Error('some issue with logic here, my dude')
      phoneCodesMap_byCountryCode.value.set(
        phoneCodeAutocompleteOption.code,
        phoneCodeAutocompleteOption
      )
    })
  }

  const initCountries = async () => {
    const VIPCountryCodes = ['FR', 'BE', 'NL', 'GB']
    const lang = document
      .querySelector('html')
      ?.getAttribute('lang')
      ?.split('-')[0]

    function swapFirstAndThird(arr) {
      if (arr.length >= 3) {
        const temp = arr[0]
        arr[0] = arr[2]
        arr[2] = temp
      }
      return arr
    }
    function moveFourthToSecond(arr) {
      if (arr.length >= 4) {
        const fourthItem = arr.splice(3, 1)[0]
        arr.splice(1, 0, fourthItem)
      }
      return arr
    }

    const countriesForLocale = await import(
      `i18n-iso-countries/langs/${lang}.json`
    )
    if (!countriesForLocale)
      throw new Error(`country json doesn't exist for lang: ${lang}`)
    countries.registerLocale(countriesForLocale)

    // delete countriesForLocale.countries.PS // delete this country, not existing

    const countryNamesArr: [string, string | string[]][] = Object.entries(
      countriesForLocale.countries
    )

    countryNamesArr.forEach(entry => {
      const code = entry[0]
      const country = entry[1]
      let label = ''
      if (Array.isArray(country)) {
        country.forEach((nameVariant, nameVariant_i) => {
          if (nameVariant_i === 0) label = nameVariant
          else label = label + ' / ' + nameVariant
        })
      } else {
        label = country
      }
      const countryAutocompleteOption = new CountryAutocompleteOption(
        label,
        label
      )
      if (VIPCountryCodes.includes(code)) {
        countries_prepared.value.unshift(countryAutocompleteOption)
      } else {
        countries_prepared.value.push(countryAutocompleteOption)
      }
      countries_prepared_map.value.set(
        countryAutocompleteOption.value,
        countryAutocompleteOption
      )
    })

    countries_prepared.value = swapFirstAndThird(countries_prepared.value)
    countries_prepared.value = moveFourthToSecond(countries_prepared.value)
  }

  const setDefaultsIfNeeded = () => {
    if (!props.form.country || !props.form.country) {
      const formClone = _.cloneDeep(props.form)
      const lang = document.querySelector('html')?.getAttribute('lang')
      if (!lang) throw new Error('no lang on html?')
      if (!props.form.phone_code) {
        const defaultCountryCode: string | undefined = defaultPhoneCodes[lang]
        formClone.phone_code =
          defaultCountryCode || phone_codes_prepared.value[0]?.value
      }
      if (!props.form.country) {
        const defaultCountry: string | undefined = defaultCountries[lang]
        formClone.country = defaultCountry || countries_prepared.value[0]?.value
      }
      emit('updateForm', formClone)
    }
  }

  onMounted(async () => {
    await Promise.all([initPhoneCodes(), initCountries()])
    setDefaultsIfNeeded()
  })

return (_ctx: any,_cache: any) => {
  return (props.form)
    ? (_openBlock(), _createElementBlock("form", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)('loginRegister.user_info_to_contact_you')), 1)
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(TextInput, {
            id: 'address',
            value: props.form.address,
            placeholder: contentProcessed.value['address'],
            "container-box-border": 
          props.showErrors && props.invalidFields.has('address')
            ? '1px solid var(--primary-orange)'
            : undefined
        ,
            onInput: _cache[0] || (_cache[0] = newVal => handleTextInputChange('address', newVal))
          }, {
            after: _withCtx(() => [
              (!_ctx.invalidFields.has('address') && props.form.address)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createVNode(CheckSvg, {
                      height: 16,
                      width: 16,
                      color: 'var(--primary-green)'
                    })
                  ]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["value", "placeholder", "container-box-border"]),
          (props.showErrors && props.invalidFields.has('address'))
            ? (_openBlock(), _createBlock(FieldError, {
                key: 0,
                text: _unref(t)('loginRegister.user_info_mandatory_field')
              }, null, 8, ["text"]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(TextInput, {
            id: 'city',
            value: props.form.city,
            placeholder: contentProcessed.value['city'],
            "container-box-border": 
          props.showErrors && props.invalidFields.has('city')
            ? '1px solid var(--primary-orange)'
            : undefined
        ,
            onInput: _cache[1] || (_cache[1] = newVal => handleTextInputChange('city', newVal))
          }, {
            after: _withCtx(() => [
              (!_ctx.invalidFields.has('city') && props.form.city)
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    _createVNode(CheckSvg, {
                      height: 16,
                      width: 16,
                      color: 'var(--primary-green)'
                    })
                  ]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["value", "placeholder", "container-box-border"]),
          (props.showErrors && props.invalidFields.has('city'))
            ? (_openBlock(), _createBlock(FieldError, {
                key: 0,
                text: _unref(t)('loginRegister.user_info_mandatory_field')
              }, null, 8, ["text"]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("div", _hoisted_9, [
            _createVNode(TextInput, {
              id: 'postal_code',
              value: props.form.postal_code,
              placeholder: contentProcessed.value['postal_code'],
              "container-box-border": 
            props.showErrors && props.invalidFields.has('postal_code')
              ? '1px solid var(--primary-orange)'
              : undefined
          ,
              onInput: _cache[2] || (_cache[2] = newVal => handleTextInputChange('postal_code', newVal))
            }, {
              after: _withCtx(() => [
                (!_ctx.invalidFields.has('postal_code') && props.form.postal_code)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                      _createVNode(CheckSvg, {
                        height: 16,
                        width: 16,
                        color: 'var(--primary-green)'
                      })
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["value", "placeholder", "container-box-border"]),
            (props.showErrors && props.invalidFields.has('postal_code'))
              ? (_openBlock(), _createBlock(FieldError, {
                  key: 0,
                  text: _unref(t)('loginRegister.user_info_mandatory_field')
                }, null, 8, ["text"]))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_11, [
            _createVNode(Autocomplete, {
              id: 'country',
              options: countries_prepared.value,
              "selected-option": selectedCountryOption.value,
              "filter-fields": ['value', 'label'],
              onSelectClick: _cache[3] || (_cache[3] = option => handleCountryChange(option))
            }, null, 8, ["options", "selected-option"])
          ])
        ]),
        _createElementVNode("div", _hoisted_12, [
          _createVNode(ContainerBox, {
            border: 
          props.showErrors &&
          (props.invalidFields.has('phone_code') ||
            props.invalidFields.has('phone_number'))
            ? '1px solid var(--primary-orange)'
            : undefined
        ,
            style: {"overflow":"visible"}
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_13, [
                _createElementVNode("div", {
                  class: "col-6 col-md-4",
                  style: _normalizeStyle({
              'border-right':
                props.showErrors &&
                (props.invalidFields.has('phone_code') ||
                  props.invalidFields.has('phone_number'))
                  ? '1px solid var(--primary-orange)'
                  : '1px solid #DEE2E6',
            })
                }, [
                  _createVNode(Autocomplete, {
                    id: 'phone_code',
                    options: phone_codes_prepared.value,
                    "selected-option": selectedPhoneCodeOption.value,
                    "filter-fields": ['name', 'dial_code', 'code', 'value', 'label'],
                    border: '1px solid transparent',
                    containerBorderRadius: '10px 0px 0px 10px',
                    onSelectClick: _cache[4] || (_cache[4] = option => handlePhoneCodeChange(option))
                  }, null, 8, ["options", "selected-option"])
                ], 4),
                _createElementVNode("div", _hoisted_14, [
                  _createVNode(TextInput, {
                    id: 'phone_number',
                    class: "h-100",
                    value: props.form.phone_number,
                    placeholder: contentProcessed.value['phone_number'],
                    "container-box-border": 'none',
                    containerBorderRadius: '0px 10px 10px 0px',
                    inputType: 'tel',
                    "allowed-type": 'number',
                    onInput: _cache[5] || (_cache[5] = 
                newVal => {
                  // Regular expression to check if the string contains only numbers
                  const onlyNumbers = /^[0-9]*$/

                  if (onlyNumbers.test(newVal)) {
                    handleTextInputChange('phone_number', newVal)
                  }
                }
              )
                  }, {
                    after: _withCtx(() => [
                      (
                    !_ctx.invalidFields.has('phone_number') &&
                    props.form.phone_number &&
                    _unref(isPhoneNumberValid)(props.form.phone_number, props.form.phone_code)
                  )
                        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                            _createVNode(CheckSvg, {
                              height: 16,
                              width: 16,
                              color: 'var(--primary-green)'
                            })
                          ]))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  }, 8, ["value", "placeholder"])
                ])
              ])
            ]),
            _: 1
          }, 8, ["border"]),
          (props.showErrors && props.invalidFields.has('phone_code'))
            ? (_openBlock(), _createBlock(FieldError, {
                key: 0,
                text: _unref(t)('loginRegister.user_info_mandatory_field')
              }, null, 8, ["text"]))
            : _createCommentVNode("", true),
          (props.showErrors && props.invalidFields.has('phone_number'))
            ? (_openBlock(), _createBlock(FieldError, {
                key: 1,
                text: invalidPhoneMessage.value
              }, null, 8, ["text"]))
            : _createCommentVNode("", true)
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})